// The portal's one-time "Choose a moderation policy" dialog, for the project created automatically
// at first sign-in. Decided from the portal layout's data, so it renders with the page.

import type { ModerationProfile } from "shared/ModerationProfile";
import { DEFAULT_MODERATION_PROFILE_ID } from "shared/PolicyPresetIds";
import type { Project } from "shared/Project";
import type { AuthState } from "shared/UtilTypes";
import { getRequiredTermsStatus } from "src/lib/TermsAcceptance";

export interface ModerationPolicyChoiceInput {
  authState: AuthState | undefined;
  projects: { items: Project[] } | undefined;
  selectedProjectId: string | null | undefined;
  moderationProfiles: ModerationProfile[] | undefined;
}

/**
 * The project whose built-in policy the signed-in person should choose, or undefined when there's
 * nothing to ask: only their own automatic default project, while it's selected and still follows
 * General, before they chose or dismissed the question and after the Terms are accepted.
 * Staff impersonating an account are never asked.
 */
export const getModerationPolicyChoiceProject = (
  input: ModerationPolicyChoiceInput,
): Project | undefined => {
  const { authState, projects, selectedProjectId, moderationProfiles } = input;
  if (!authState?.user || authState.impersonating || getRequiredTermsStatus(authState))
    return undefined;
  const onboarding = authState.user.onboarding;
  if (!onboarding?.defaultProjectId || onboarding.moderationPolicyChosenAt != null)
    return undefined;
  if (selectedProjectId !== onboarding.defaultProjectId) return undefined;
  const project = projects?.items.find((item) => item.id === onboarding.defaultProjectId);
  if (!project) return undefined;
  const policy = moderationProfiles?.find(
    (profile) => profile.id === project.settings?.moderationProfileId,
  );
  return policy?.followsPreset &&
    policy.preset?.id === DEFAULT_MODERATION_PROFILE_ID &&
    policy.preset.projectId === project.id
    ? project
    : undefined;
};

/** The project's own reference to a built-in policy, which it follows until it's edited. */
export const findPresetReference = (
  moderationProfiles: ModerationProfile[],
  projectId: string,
  presetId: string,
) =>
  moderationProfiles.find(
    (profile) =>
      profile.followsPreset &&
      profile.preset?.id === presetId &&
      profile.preset.projectId === projectId,
  );
